<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { BugOutlined, LoginOutlined, SearchOutlined } from '@ant-design/icons-vue'

import { useAuthStore } from '@/stores/auth'

type DemoUserType = 'single' | 'multi' | 'partner' | 'vision' | 'multi-orgs' | 'admin'

type DevCommand = {
  id: string
  title: string
  description?: string
  keywords?: string[]
  disabled?: boolean
  run: () => Promise<void> | void
}

const router = useRouter()
const { logout, loginWithCredentials } = useAuthStore()

const isOpen = ref(false)
const search = ref('')
const selectedIndex = ref(0)
const isLoading = ref(false)
const searchInputRef = ref<HTMLInputElement | null>(null)
const commandListRef = ref<HTMLElement | null>(null)

async function switchLoggedInUser(userType: DemoUserType) {
  const demoUserEmail = 'demo@ruminati.com.au'
  const splitScenarioEmail = demoUserEmail.split('@')

  try {
    isLoading.value = true

    // log the user out if not on /login, otherwise assume they are logged out
    if (router.currentRoute.value.path !== '/login') {
      await logout()
    }

    const email = `${splitScenarioEmail[0]}+${userType}@${splitScenarioEmail[1]}`
    const password = 'Testing123'
    await loginWithCredentials({ email, password })

    await router.push('/')
    closePalette()
  } catch (err) {
    console.log({ err })
  } finally {
    isLoading.value = false
  }
}

const commands: DevCommand[] = [
  {
    id: 'switch-user-single',
    title: 'Switch user: Emissions Single Enterprise User',
    description: 'Login as demo+single@ruminati.com.au',
    keywords: ['demo', 'login', 'single', 'enterprise', 'emissions'],
    run: () => switchLoggedInUser('single'),
  },
  {
    id: 'switch-user-multi',
    title: 'Switch user: Emissions Multi Enterprise User',
    description: 'Login as demo+multi@ruminati.com.au',
    keywords: ['demo', 'login', 'multi', 'enterprise', 'emissions'],
    run: () => switchLoggedInUser('multi'),
  },
  {
    id: 'switch-user-partner',
    title: 'Switch user: Partner Org User',
    description: 'Login as demo+partner@ruminati.com.au',
    keywords: ['demo', 'login', 'partner', 'organisation'],
    run: () => switchLoggedInUser('partner'),
  },
  {
    id: 'switch-user-vision',
    title: 'Switch user: Vision Org User',
    description: 'Login as demo+vision@ruminati.com.au',
    keywords: ['demo', 'login', 'vision', 'organisation'],
    run: () => switchLoggedInUser('vision'),
  },
  {
    id: 'switch-user-admin',
    title: 'Switch user: Support Admin',
    description: 'Login as demo+admin@ruminati.com.au',
    keywords: ['demo', 'login', 'admin', 'support'],
    run: () => switchLoggedInUser('admin'),
  },
  {
    id: 'clear-local-storage',
    title: 'Clear local storage',
    description: 'Dummy first-pass command; not wired up yet',
    keywords: ['storage', 'reset', 'dummy'],
    disabled: true,
    run: () => {},
  },
  {
    id: 'seed-demo-data',
    title: 'Seed demo data',
    description: 'Dummy first-pass command; not wired up yet',
    keywords: ['seed', 'demo', 'scenario', 'dummy'],
    disabled: true,
    run: () => {},
  },
]

const filteredCommands = computed(() => {
  const query = search.value.trim().toLowerCase()

  if (!query) {
    return commands
  }

  return commands.filter((command) => {
    const haystack = [command.title, command.description, ...(command.keywords ?? [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(query)
  })
})

function openPalette() {
  isOpen.value = true
}

function closePalette() {
  isOpen.value = false
  search.value = ''
  selectedIndex.value = 0
}

async function runCommand(command: DevCommand) {
  if (command.disabled || isLoading.value) {
    return
  }

  await command.run()
}

function runSelectedCommand() {
  const command = filteredCommands.value[selectedIndex.value]

  if (command) {
    void runCommand(command)
  }
}

async function scrollSelectedCommandIntoView() {
  await nextTick()

  const selectedCommandElement =
    commandListRef.value?.querySelector<HTMLElement>('[data-selected="true"]')
  selectedCommandElement?.scrollIntoView({ block: 'nearest' })
}

function handleGlobalKeydown(event: KeyboardEvent) {
  const isCommandK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k'

  if (isCommandK) {
    event.preventDefault()

    if (isOpen.value) {
      closePalette()
    } else {
      openPalette()
    }

    return
  }

  if (!isOpen.value) {
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    event.stopPropagation()
    selectedIndex.value = Math.min(selectedIndex.value + 1, filteredCommands.value.length - 1)
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    event.stopPropagation()
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    event.stopPropagation()
    runSelectedCommand()
  }
}

watch(isOpen, async (open) => {
  if (open) {
    await nextTick()
    searchInputRef.value?.focus()
  }
})

watch(filteredCommands, () => {
  selectedIndex.value = 0
})

watch(selectedIndex, () => {
  void scrollSelectedCommandIntoView()
})

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleGlobalKeydown, true)
})
</script>

<template>
  <a-modal
    v-model:open="isOpen"
    :footer="null"
    :closable="false"
    :width="640"
    centered
    destroy-on-close
    wrap-class-name="dev-command-palette-modal"
    @cancel="closePalette"
  >
    <div class="overflow-hidden rounded-lg bg-white">
      <div
        class="flex items-center gap-3 border-0 border-b border-solid border-slate-200 px-4 py-3"
      >
        <SearchOutlined class="text-slate-400" />
        <input
          ref="searchInputRef"
          v-model="search"
          class="dev-command-palette-input min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-none"
          placeholder="Search dev commands..."
          :disabled="isLoading"
        />
        <a-tag>⌘K</a-tag>
      </div>

      <div ref="commandListRef" class="max-h-96 overflow-y-auto p-2">
        <div v-if="filteredCommands.length === 0" class="px-4 py-8 text-center text-slate-500">
          No dev commands found.
        </div>

        <button
          v-for="(command, index) in filteredCommands"
          :key="command.id"
          type="button"
          class="dev-command-palette-item w-full cursor-pointer rounded-md border-0 px-3 py-3 text-left"
          :class="[command.disabled ? 'cursor-not-allowed opacity-50' : '']"
          :data-selected="index === selectedIndex"
          :disabled="command.disabled || isLoading"
          @mouseenter="selectedIndex = index"
          @click="runCommand(command)"
        >
          <div class="flex items-start gap-3">
            <LoginOutlined
              v-if="command.id.startsWith('switch-user')"
              class="mt-1 text-slate-500"
            />
            <BugOutlined v-else class="mt-1 text-slate-500" />
            <div class="min-w-0 flex-1">
              <div class="font-medium text-slate-900">
                {{ command.title }}
              </div>
              <div v-if="command.description" class="mt-1 text-xs text-slate-500">
                {{ command.description }}
              </div>
            </div>
          </div>
        </button>
      </div>

      <div
        class="flex items-center justify-between border-0 border-t border-solid border-slate-200 px-4 py-2 text-xs text-slate-500"
      >
        <span>↑↓ Navigate · Enter Run · Esc Close</span>
        <a-spin v-if="isLoading" size="small" />
      </div>
    </div>
  </a-modal>
</template>

<style scoped>
.dev-command-palette-input {
  font-size: 16px;
}

.dev-command-palette-item {
  background-color: transparent;
}

.dev-command-palette-item:hover,
.dev-command-palette-item[data-selected='true'] {
  background-color: rgb(241 245 249);
}
</style>
